import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { CartItem } from '@rosita/shared';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useCartStore } from '../../stores/cartStore';
import { formatEur } from '../../lib/format';
import { t } from '../../i18n/pt';
import styles from './CartConfirmation.module.scss';

const AUTO_DISMISS_MS = 6000;
const EXIT_ANIMATION_MS = 240; // matches --dur-base

interface ShowArgs {
  item: CartItem;
  triggerEl?: HTMLElement | null;
}

interface CartConfirmationContextValue {
  show: (args: ShowArgs) => void;
  dismiss: () => void;
}

const CartConfirmationContext =
  createContext<CartConfirmationContextValue | null>(null);

export function useCartConfirmation(): CartConfirmationContextValue {
  const ctx = useContext(CartConfirmationContext);
  if (!ctx) {
    throw new Error(
      'useCartConfirmation must be used within <CartConfirmationProvider>',
    );
  }
  return ctx;
}

interface PanelState {
  item: CartItem;
  triggerEl: HTMLElement | null;
  /** bumped on every show() so the panel can reset its 6s timer */
  showId: number;
}

export function CartConfirmationProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [state, setState] = useState<PanelState | null>(null);
  const nextShowIdRef = useRef(1);

  const dismiss = useCallback((): void => {
    setState(null);
  }, []);

  const show = useCallback((args: ShowArgs): void => {
    setState({
      item: args.item,
      triggerEl: args.triggerEl ?? null,
      showId: nextShowIdRef.current++,
    });
  }, []);

  const value = useMemo<CartConfirmationContextValue>(
    () => ({ show, dismiss }),
    [show, dismiss],
  );

  return (
    <CartConfirmationContext.Provider value={value}>
      {children}
      {state ? (
        <AddedToCartPanel
          key="cart-confirmation-panel"
          item={state.item}
          triggerEl={state.triggerEl}
          showId={state.showId}
          onDismiss={dismiss}
        />
      ) : null}
    </CartConfirmationContext.Provider>
  );
}

interface PanelProps {
  item: CartItem;
  triggerEl: HTMLElement | null;
  showId: number;
  onDismiss: () => void;
}

function AddedToCartPanel({
  item,
  triggerEl,
  showId,
  onDismiss,
}: PanelProps): JSX.Element {
  const cartCount = useCartStore((s) => s.getItemCount());
  const headingId = useId();

  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const mouseInsideRef = useRef(false);
  const focusInsideRef = useRef(false);
  // Programmatic focus on mount also fires onFocus; ignore it until armed.
  const focusArmedRef = useRef(false);
  const triggerRef = useRef(triggerEl);

  useEffect(() => {
    triggerRef.current = triggerEl;
  }, [triggerEl]);

  // Enter animation on first mount.
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Focus the close button when the panel first appears (and arm the
  // pause-on-focus handler afterwards).
  useEffect(() => {
    closeBtnRef.current?.focus();
    const id = window.setTimeout(() => {
      focusArmedRef.current = true;
    }, 100);
    return () => window.clearTimeout(id);
  }, []);

  const clearTimer = useCallback((): void => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const beginExit = useCallback((): void => {
    if (exiting) return;
    setExiting(true);
    setVisible(false);
    clearTimer();
    window.setTimeout(() => {
      const trigger = triggerRef.current;
      if (trigger && document.body.contains(trigger)) {
        trigger.focus();
      }
      onDismiss();
    }, EXIT_ANIMATION_MS);
  }, [exiting, clearTimer, onDismiss]);

  const startTimer = useCallback((): void => {
    clearTimer();
    if (mouseInsideRef.current || focusInsideRef.current) return;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      beginExit();
    }, AUTO_DISMISS_MS);
  }, [beginExit, clearTimer]);

  // (Re)start the 6s timer every time show() fires.
  useEffect(() => {
    startTimer();
    return clearTimer;
  }, [showId, startTimer, clearTimer]);

  // Escape dismisses regardless of where focus currently is.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        beginExit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [beginExit]);

  const handleMouseEnter = (): void => {
    mouseInsideRef.current = true;
    clearTimer();
  };

  const handleMouseLeave = (): void => {
    mouseInsideRef.current = false;
    if (!focusInsideRef.current) startTimer();
  };

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>): void => {
    if (!focusArmedRef.current) return;
    const prev = e.relatedTarget as Node | null;
    if (prev && panelRef.current?.contains(prev)) return;
    focusInsideRef.current = true;
    clearTimer();
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
    const next = e.relatedTarget as Node | null;
    if (next && panelRef.current?.contains(next)) return;
    focusInsideRef.current = false;
    if (!mouseInsideRef.current) startTimer();
  };

  const variantDetails = [item.size, item.colour].filter(Boolean).join(' · ');
  const formattedPrice = formatEur(item.price * item.quantity);

  return (
    <>
      <div
        className={`${styles.scrim} ${visible && !exiting ? styles.visible : ''}`}
        aria-hidden="true"
        onClick={beginExit}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-labelledby={headingId}
        className={`${styles.panel} ${visible && !exiting ? styles.visible : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={-1}
      >
        <div className={styles.header}>
          <span className={styles.headerIcon} aria-hidden="true">
            <Icon name="check" size={16} />
          </span>
          <h2 id={headingId} className={styles.title}>
            {t.cart.added}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            className={styles.close}
            aria-label={t.nav.close}
            onClick={beginExit}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className={styles.summary}>
          <div className={styles.thumb}>
            {item.imageUrl ? <img src={item.imageUrl} alt="" /> : null}
          </div>
          <div className={styles.summaryInfo}>
            <span className={styles.productName}>{item.name}</span>
            {variantDetails ? (
              <span className={styles.variant}>{variantDetails}</span>
            ) : null}
            <span className={styles.price}>{formattedPrice}</span>
          </div>
        </div>

        <div className={styles.actions}>
          <Button variant="primary" size="md" fullWidth to="/checkout" onClick={beginExit}>
            {t.cart.checkout}
          </Button>
          <Button variant="secondary" size="md" fullWidth to="/carrinho" onClick={beginExit}>
            {`${t.cart.viewCart} (${cartCount})`}
          </Button>
        </div>
      </div>
    </>
  );
}
